import * as React from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';

function keepDialogFocusVisible(active: Element | null, content: HTMLDivElement) {
  const paper = content.closest('.MuiDialog-paper');
  const viewport = content.closest('.MuiDialog-container');
  if (
    !(active instanceof HTMLElement) ||
    !active.matches('input, button') ||
    !paper?.contains(active) ||
    !viewport
  )
    return;
  const target = active.closest('label') ?? active;
  const rect = target.getBoundingClientRect();
  const bounds = viewport.getBoundingClientRect();
  if (rect.top < bounds.top || rect.bottom > bounds.bottom) {
    target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }
}

// Visit-only settings live on the root element. During prerendering there is no document, and
// a fresh page load has none of these classes, so the server and first browser render agree.
const hasRootClass = (name: string) =>
  typeof document !== 'undefined' && document.documentElement.classList.contains(name);

export default function AccessibilityOptions({ inverted = false }: { inverted?: boolean }) {
  const [optionsOpen, setOptionsOpen] = React.useState(false);
  const [content, setContent] = React.useState<HTMLDivElement | null>(null);
  const [largerText, setLargerText] = React.useState(() => hasRootClass('site-large-text'));
  const [underlinedLinks, setUnderlinedLinks] = React.useState(() =>
    hasRootClass('site-underline-links')
  );
  const [reducedMotion, setReducedMotion] = React.useState(() =>
    hasRootClass('site-reduce-motion')
  );

  React.useEffect(() => {
    document.documentElement.classList.toggle('site-large-text', largerText);
  }, [largerText]);

  React.useEffect(() => {
    document.documentElement.classList.toggle('site-underline-links', underlinedLinks);
  }, [underlinedLinks]);

  React.useEffect(() => {
    document.documentElement.classList.toggle('site-reduce-motion', reducedMotion);
  }, [reducedMotion]);

  React.useEffect(() => {
    if (!optionsOpen || !content || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      keepDialogFocusVisible(document.activeElement, content);
    });
    content.querySelectorAll('label').forEach((label) => observer.observe(label));
    observer.observe(content);
    const title = content.parentElement?.querySelector('.MuiDialogTitle-root');
    if (title) observer.observe(title);
    const paper = content.closest('.MuiDialog-paper');
    if (paper) observer.observe(paper);
    const viewport = content.closest('.MuiDialog-container');
    if (viewport) observer.observe(viewport);
    return () => observer.disconnect();
  }, [optionsOpen, content]);

  return (
    <>
      <Button
        variant='outlined'
        onClick={() => setOptionsOpen(true)}
        sx={{
          mt: 2,
          ...(inverted ? { color: '#FFFFFF', borderColor: '#CBD5E1' } : {}),
        }}
      >
        Accessibility options
      </Button>
      <Dialog
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        aria-labelledby='accessibility-options-title'
        scroll='body'
        sx={{ '& .MuiDialog-container': { scrollPaddingBlock: '24px' } }}
        onFocus={(event) => {
          if (content) keepDialogFocusVisible(event.target, content);
        }}
      >
        <DialogTitle id='accessibility-options-title'>Accessibility options</DialogTitle>
        <DialogContent ref={setContent}>
          <FormControlLabel
            control={
              <Switch
                checked={largerText}
                onChange={(event) => setLargerText(event.target.checked)}
              />
            }
            label='Larger text for this visit'
          />
          <FormControlLabel
            control={
              <Switch
                checked={underlinedLinks}
                onChange={(event) => setUnderlinedLinks(event.target.checked)}
              />
            }
            label='Underline links for this visit'
          />
          <FormControlLabel
            control={
              <Switch
                checked={reducedMotion}
                onChange={(event) => setReducedMotion(event.target.checked)}
              />
            }
            label='Reduce motion for this visit'
          />
          <Typography variant='body2'>
            You can also use your browser&apos;s zoom and your device&apos;s accessibility settings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
